import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { palette } from '@m12s/component-library';
import { DefaultCardBorder } from 'components/MachineHeader/styled';

const MOBILE_WIDTH = '720px';

const ContentContainer = styled.section`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  /* Two content rows (KPI cards + spanning detail card) — a third template
     row would reserve phantom height and force a needless scrollbar. minmax
     lets the detail card grow with its content, and align-content pins the
     rows to their natural heights (Figma: empty grey area below the cards)
     instead of stretching them across the leftover space. */
  grid-template-rows: 7.875rem minmax(8.75rem, auto);
  align-content: start;
  gap: 1rem;
  padding: 1.5rem;
  background-color: ${palette.Grey200};
  /* Flex-fill between the header and the View Documents footer; auto (not
     'scroll') so scrollbars only appear when the content actually overflows. */
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  @media (max-width: ${MOBILE_WIDTH}) {
    display: flex;
    flex-wrap: wrap;
  }
`;

const TopCardContainer = styled.div`
  padding: 1.5rem;
  background-color: ${palette.White};
  width: 100%;
  color: ${palette.Grey700};
  ${DefaultCardBorder}
`;

const Label = styled.h5`
  font-weight: 400;
  color: ${palette.Grey700};
  font-size: 0.75rem;
  line-height: 0.75rem;
  margin: 0;
  margin-bottom: 2px;
`;

const Value = styled.div`
  color: ${palette.Grey700};
  font-weight: 600;
  font-size: 28px;
  line-height: 36px;
  margin-bottom: 0.5rem;
  white-space: nowrap;
`;

// Figma: card values are Noto Sans semibold 16px Grey/700 (labels stay 12px
// regular via Label above).
const SubValue = styled.div`
  margin-top: auto;
  font-size: 16px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.2px;
  color: ${palette.Grey700};
`;

// The PCM edit pencil beside the "Part Counting" label. Icon-only affordance
// (Indigo600) matching the Production Run Details card.
const OpButtonBase = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${palette.Indigo600};
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
  &:focus-visible {
    outline: 2px solid ${palette.Indigo600};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

// A span with onClick is invisible to keyboard and screen-reader users. This is
// a real button in behavior (icon-only affordance + text-label variants), so
// give it button semantics and keyboard activation while keeping the inline
// styling above. Callers keep passing onClick/children/aria-label unchanged.
const OpButton = ({ onClick, onKeyDown, children, ...rest }) => {
  const handleKeyDown = (event) => {
    // Space and Enter activate a native button; mirror that for this span.
    if (onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick(event);
    }
    if (onKeyDown) onKeyDown(event);
  };

  return (
    <OpButtonBase
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {children}
    </OpButtonBase>
  );
};

OpButton.propTypes = {
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  children: PropTypes.node,
};

// Disabled state uses the shared @m12s Tooltip (click-to-reveal reason), but its
// built-in trigger button is a 2.75rem grey box that turns blue on focus — which
// inflates this KPI card's label row and clashes. This wrapper tames that inner
// button back to the same compact, greyed, non-interactive footprint as OpButton
// so the card layout is identical across states.
const DisabledPcmTooltip = styled.span`
  display: inline-flex;
  align-items: center;

  button {
    display: inline-flex;
    align-items: center;
    height: auto;
    width: auto;
    padding: 0;
    color: ${palette.Grey400};
    cursor: not-allowed;
  }
  /* Don't recolor the icon blue on focus — it's a disabled affordance. */
  button:focus svg {
    color: ${palette.Grey400};
  }
`;

// Label + optional trailing accessory (the PCM edit pencil) on one baseline,
// matching the Production Run Details card where the pencil sits beside the label.
const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 2px;
`;

const TopCard = ({ label, labelAccessory, value, subValue }) => {
  return (
    <TopCardContainer>
      <LabelRow>
        <Label style={{ marginBottom: 0 }}>{label}</Label>
        {labelAccessory}
      </LabelRow>
      <Value>{value}</Value>
      <SubValue>{subValue}</SubValue>
    </TopCardContainer>
  );
};

TopCard.propTypes = {
  label: PropTypes.string,
  labelAccessory: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

// Picking-flow variant of TopCard: fixed-height flex card with the sub-value
// centered under the value and long values truncated with an ellipsis.
const PickingCardContainer = styled(TopCardContainer)`
  min-height: 8rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;

  ${Label} {
    line-height: 1rem;
    margin-bottom: 0;
  }
`;

const PickingValueWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  justify-content: center;
  flex: 1;
`;

const PickingValue = styled(Value)`
  margin-bottom: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PickingSubValue = styled.div`
  font-size: 1rem;
  line-height: 1rem;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PickingTopCard = ({ label, value, subValue }) => {
  return (
    <PickingCardContainer>
      <Label>{label}</Label>
      <PickingValueWrapper>
        <PickingValue>{value}</PickingValue>
        {subValue != null && <PickingSubValue>{subValue}</PickingSubValue>}
      </PickingValueWrapper>
    </PickingCardContainer>
  );
};

PickingTopCard.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

const BottomCardContainer = styled(TopCardContainer)`
  grid-column: 1 / 5;
  display: grid;
  grid-template-columns: 1fr 1px 1fr 1px 1fr;
  align-items: space-between;
  gap: 2.5rem;
  color: ${palette.Grey700};
  @media (max-width: ${MOBILE_WIDTH}) {
    grid-template-rows: 1fr 1px 1fr 1px 1fr;
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const InfoGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  @media (max-width: ${MOBILE_WIDTH}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
`;

// Figma: the detail-card items breathe more than the KPI cards — 8px between
// the label and its value (the KPI cards keep Label's tight 2px).
const InfoValue = styled(SubValue)`
  margin-top: 0.5rem;
`;

const Info = ({ label, value }) => {
  return (
    <div
      style={{
        minWidth: '3rem',
      }}
    >
      <Label>{label}</Label>
      <InfoValue>{value}</InfoValue>
    </div>
  );
};

Info.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

const InfoGroup = ({ top, bottom }) => {
  const bottomIsArray = Array.isArray(bottom);

  return (
    <InfoGroupContainer>
      <Info {...top} />
      {bottomIsArray ? (
        <div style={{ display: 'flex', gap: '.5rem' }}>
          {bottom.map((item) => {
            return <Info {...item} isArray key={item.label} />;
          })}
        </div>
      ) : (
        <Info {...bottom} />
      )}
    </InfoGroupContainer>
  );
};

InfoGroup.propTypes = {
  top: PropTypes.object,
  bottom: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

const Divider = styled.div`
  width: 1px;
  height: 100%;
  background-color: ${palette.Grey300};
  @media (max-width: ${MOBILE_WIDTH}) {
    width: 100%;
    height: 1px;
  }
`;

const Footer = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 4.5rem;
  background-color: ${palette.White};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid ${palette.Grey400};
`;

// Flex variant used by the Pennex materials flow (tabs need to fill remaining height)
const FlexContentContainer = styled.section`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  background-color: ${palette.Grey200};
  overflow: hidden;
`;

const TopCardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  padding: 1.5rem 1.5rem 0;
  flex-shrink: 0;
  background-color: ${palette.Grey200};
`;

export {
  OpButton,
  DisabledPcmTooltip,
  Footer,
  ContentContainer,
  FlexContentContainer,
  TopCardsRow,
  TopCard,
  PickingTopCard,
  BottomCardContainer,
  InfoGroup,
  Divider,
};
